import { getSettings } from '../../utils/utilities';
import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { PRIDeviceType } from '../../types/pri-device/pri-device.type';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import {
  deletePRIDeviceDetails,
  getPRIDeviceDetails,
  getPRIDevicesList,
  patchPRIDeviceDetails,
  patchPRIDevicePortsDetails,
  postPRIDeviceDetails,
  postPRIDeviceOwnershipTransfer,
} from './priDevices.action';
import snackbarUtils from '../../utils/SnackbarUtils';

const settings = getSettings();

export interface PRIDevicesState {
  priDevicesList: null | PaginationType<PRIDeviceType>;
  priDevicesListFetchStatus: FETCH_STATUS;
  priDevicesListRouteParams: RouteParamsType;
  priDevice: null | PRIDeviceType;
  priDeviceFetchStatus: FETCH_STATUS;
  priDevicePostStatus: FETCH_STATUS;
  priDevicePatchStatus: FETCH_STATUS;
  priDeviceDeleteStatus: FETCH_STATUS;
  priDeviceOwnershipTransferStatus: FETCH_STATUS;
}

export const initialPRIDevicesRouteParams = {
  page: 1,
  limit: settings?.limit || 10,
  order: '-pri_device.id',
  q: '',
};

const INIT_STATE: PRIDevicesState = {
  priDevicesListRouteParams: initialPRIDevicesRouteParams,
  priDevicesList: null,
  priDevicesListFetchStatus: FETCH_STATUS.NULL,
  priDevice: null,
  priDeviceFetchStatus: FETCH_STATUS.NULL,
  priDevicePostStatus: FETCH_STATUS.NULL,
  priDevicePatchStatus: FETCH_STATUS.NULL,
  priDeviceDeleteStatus: FETCH_STATUS.NULL,
  priDeviceOwnershipTransferStatus: FETCH_STATUS.NULL,
};

const priDevicesSlice = createSlice({
  name: 'pri-devices',
  initialState: INIT_STATE,
  reducers: {
    setPRIDevicesList: (state, action) => {
      state.priDevicesList = action.payload;
    },
    setPRIDevicesListFetchStatus: (state, action) => {
      state.priDevicesListFetchStatus = action.payload;
    },
    setPRIDevice: (state, action) => {
      state.priDevice = action.payload;
    },
    setPRIDeviceFetchStatus: (state, action) => {
      state.priDeviceFetchStatus = action.payload;
    },
    setPRIDevicePostStatus: (state, action) => {
      state.priDevicePostStatus = action.payload;
    },
    setPRIDevicePatchStatus: (state, action) => {
      state.priDevicePatchStatus = action.payload;
    },
    setPRIDeviceDeleteStatus: (state, action) => {
      state.priDeviceDeleteStatus = action.payload;
    },
    setPRIDeviceOwnershipTransferStatus: (state, action) => {
      state.priDeviceOwnershipTransferStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'pri-devices') {
        state.priDevicesListRouteParams = { ...state.priDevicesListRouteParams, ...payload.data };
      }
    });

    // GET PRI DEVICES LIST
    builder.addCase(getPRIDevicesList.pending, state => {
      state.priDevicesListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getPRIDevicesList.rejected, (state, action) => {
      if (action.payload) {
        state.priDevicesListFetchStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getPRIDevicesList.fulfilled, (state, action) => {
      state.priDevicesListFetchStatus = FETCH_STATUS.FULFILLED;
      state.priDevicesList = action.payload;
    });

    // GET PRI DEVICE DETAILS
    builder.addCase(getPRIDeviceDetails.pending, state => {
      state.priDeviceFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getPRIDeviceDetails.rejected, (state, action) => {
      state.priDeviceFetchStatus = FETCH_STATUS.REJECTED;
      state.priDevice = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getPRIDeviceDetails.fulfilled, (state, action) => {
      state.priDeviceFetchStatus = FETCH_STATUS.FULFILLED;
      state.priDevice = action.payload;
    });

    // CREATE PRI DEVICE DETAILS
    builder.addCase(postPRIDeviceDetails.pending, state => {
      state.priDevicePostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postPRIDeviceDetails.rejected, (state, action) => {
      state.priDevicePostStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });
    builder.addCase(postPRIDeviceDetails.fulfilled, state => {
      state.priDevicePostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('PRI Device created successfully');
      });
    });

    // DELETE PRI DEVICE DETAILS
    builder.addCase(deletePRIDeviceDetails.pending, state => {
      state.priDeviceDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deletePRIDeviceDetails.rejected, (state, action) => {
      state.priDeviceDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deletePRIDeviceDetails.fulfilled, (state, action) => {
      if (action.payload.fetchList) {
        state.priDeviceDeleteStatus = FETCH_STATUS.NULL;
      } else {
        state.priDeviceDeleteStatus = FETCH_STATUS.FULFILLED;
      }
      setTimeout(() => {
        snackbarUtils.success('ATA has been removed!');
      });
    });

    // TRANSFER PRI DEVICE OWNERSHIP
    builder
      .addCase(postPRIDeviceOwnershipTransfer.pending, state => {
        state.priDeviceOwnershipTransferStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postPRIDeviceOwnershipTransfer.rejected, (state, action) => {
        state.priDeviceOwnershipTransferStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error((action.payload as string) ?? 'Failed to transfer PRI Device ownership'));
      })
      .addCase(postPRIDeviceOwnershipTransfer.fulfilled, state => {
        state.priDeviceOwnershipTransferStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('PRI Device ownership transferred successfully!'));
      });

    // PATCH PRI DEVICE DETAILS
    builder.addCase(patchPRIDeviceDetails.pending, state => {
      state.priDevicePatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchPRIDeviceDetails.rejected, (state, action) => {
      state.priDevicePatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchPRIDeviceDetails.fulfilled, state => {
      state.priDevicePatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('PRI Device updated successfully');
      });
    });

    // PATCH PRI DEVICE DETAILS
    builder.addCase(patchPRIDevicePortsDetails.pending, state => {
      state.priDevicePatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchPRIDevicePortsDetails.rejected, (state, action) => {
      state.priDevicePatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchPRIDevicePortsDetails.fulfilled, state => {
      state.priDevicePatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('PRI Device ports updated successfully');
      });
    });
  },
});

export const {
  setPRIDevicesList,
  setPRIDevicesListFetchStatus,
  setPRIDeviceDeleteStatus,
  setPRIDevicePatchStatus,
  setPRIDevicePostStatus,
  setPRIDeviceFetchStatus,
  setPRIDevice,
  setPRIDeviceOwnershipTransferStatus,
} = priDevicesSlice.actions;

export default priDevicesSlice.reducer;
